import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import IconButton from 'LuxKit/Button/IconButton'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useCreateExperienceSelectionSession from 'hooks/ExperiencePage/useCreateExperienceSelectionSession'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import {
  ARENA_SCRIPT_SRC,
  arenaEvents,
  defaultArenaOptions,
  areAllArenaSeatsMatchedToTickets,
  getArenaCustomLegend,
  getArenaDisplaySeatPrice,
  getArenaDisplayTotalPrice,
  getArenaEventStateUpdate,
  getArenaReservationFailureReason,
  getArenaReservationHold,
  getTrolleyToken,
  hasMixedArenaReservableSeatGroups,
  loadArenaScript,
} from './arenaSeatSelectionUtils'
import { createArenaTrolleyToken, createArenaTrolleyReservation, releaseArenaTransaction } from './arenaSeatSelectionApi'
import { attachArenaMapReset } from './arenaMapReset'
import { attachArenaSeatPresentation } from './arenaSeatPresentation'
import type { ArenaSeatAnchor } from './arenaSeatPresentation'
import ArenaSeatHoverCard from './ArenaSeatHoverCard'
import ArenaBookingFooter from './ArenaBookingFooter'
import type {
  ArenaApp,
  ArenaConfigOptions,
  ArenaCreateOptions,
  ArenaCurrency,
  ArenaErrorType,
  ArenaEventName,
  ArenaEventPayload,
  ArenaReservationFailureReason,
  ArenaReservationHold,
  ArenaSelectedSeat,
  ArenaSelectionResult,
  ArenaSendMethod,
  ArenaSeatAvailabilityLegend,
  ArenaStatus,
} from './arenaSeatSelectionUtils'

interface ArenaErrorState {
  type: ArenaErrorType
  eventName?: ArenaEventName
  reservationFailureReason?: ArenaReservationFailureReason
}

interface Props {
  apiBase: string
  displayCurrencyCode: string
  eventId: string
  experienceId: string
  onSelectionComplete: (result: ArenaSelectionResult) => void
  perfId: string
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot
  tyrBase: string
  options?: ArenaConfigOptions
  scriptSrc?: string
}

declare global {
  interface Window {
    IngressoArena?: {
      create: (options: ArenaCreateOptions) => Promise<ArenaApp>
    }
  }
}

const Container = styled.section`
  width: 100%;
`

const AvailabilityHeading = styled(Heading)`
  margin-bottom: ${rem(16)};
  padding-inline: ${rem(16)};
`

const Shell = styled.div`
  width: 100vw;
  margin-inline: calc(50% - 50vw);
  overflow: hidden;
  background-color: var(--palette-neutral-surface-light-background);

  ${mediaQueryUp.tablet} {
    width: 100%;
    margin-inline: 0;
    border-radius: ${props => props.theme.borderRadius.M};
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(16)} ${rem(16)} ${rem(12)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)};
  }
`

const ErrorPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  align-self: flex-start;
  padding: ${rem(6)} ${rem(10)};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.messaging.warning.lightBackground};
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: 0 ${rem(16)} ${rem(16)};

  ${mediaQueryUp.tablet} {
    padding: 0 ${rem(16)} ${rem(16)};
  }
`

const ArenaPanel = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  border-radius: ${props => props.theme.borderRadius.L};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  overflow: hidden;

  igo-arena {
    display: block;
    width: 100%;
    min-height: var(--igo-map-height);
    --igo-map-height: clamp(${rem(380)}, 50dvh, ${rem(480)});
    --igo-map-width: 100%;
  }
`

const MapLoading = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
`

const ArenaLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(12)} ${rem(16)};
  align-items: center;
  justify-content: center;
  min-height: ${rem(40)};
  padding: ${rem(10)} ${rem(16)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const ArenaLegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${rem(8)};

  > *:not(:first-child) {
    display: flex;
    align-items: center;
    min-height: ${rem(20)};
  }
`

const SelectedSeatsSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`

const SeatList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  margin: ${rem(24)} 0 0;
  padding: 0;
  list-style: none;
`

const SeatItem = styled.li`
  display: grid;
  grid-template-columns: ${rem(12)} minmax(0, 1fr) auto ${rem(40)};
  align-items: center;
  gap: ${rem(20)};
  padding: ${rem(16)} ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.L};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const SeatDetails = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${rem(2)};
`

const SeatPrice = styled(BodyText)`
  white-space: nowrap;

  && {
    font-size: 20px;
    font-weight: 450;
    line-height: 24px;
  }
`

const SeatRemoveButton = styled(IconButton)`
  --icon-button-size: ${rem(32)};
  --icon-button-icon-size: ${rem(20)};
  --button-internal-default-color: var(--palette-neutral-surface-light-foreground-alt);
  --button-internal-default-background-color: var(--palette-neutral-surface-static-white);
  --button-internal-default-border-color: var(--palette-neutral-surface-light-foreground);
`

const ColourSwatch = styled.span<{ $bgColor?: string, $borderColor?: string, $size?: number, $restricted?: boolean }>`
  background-image: ${props => props.$restricted ? 'linear-gradient(to right, #0003 50%, transparent 50%)' : 'none'};
  display: block;
  flex: none;
  width: ${props => rem(props.$size ?? 12)};
  height: ${props => rem(props.$size ?? 12)};
  border-radius: 50%;
  background-color: ${props => props.$bgColor ?? props.theme.palette.neutral.default.five};
  border: 1px solid ${props => props.$borderColor ?? props.theme.palette.neutral.default.four};
`

function getErrorLabel(intl: ReturnType<typeof useIntl>, error: ArenaErrorState) {
  switch (error.type) {
    case 'arena-api-unavailable':
      return intl.formatMessage({
        id: '_AJJfktgKzPjEEujOeKx',
        defaultMessage: 'Arena script loaded, but the Arena API was not available.',
        description: 'Error shown when the Ingresso Arena script loads without exposing the expected browser API',
      })
    case 'arena-create-failed':
      return intl.formatMessage({
        id: 'r_9PKLa6KxAUB3-k5JEo',
        defaultMessage: 'Arena could not start the seat map.',
        description: 'Error shown when the Ingresso Arena seat map cannot be created',
      })
    case 'event-failed':
      return intl.formatMessage({
        id: '-ODJG8XIuM-zmzMZmqsx',
        defaultMessage: 'Arena event failed: {eventName}',
        description: 'Error shown when Ingresso Arena emits a failure event',
      }, { eventName: error.eventName })
    case 'missing-trolley-token':
      return intl.formatMessage({
        id: 'BMpk4KKAZzlvYWJ_BAFQ',
        defaultMessage: 'We couldn’t prepare those seats for checkout. Please choose different seats and try again.',
        description: 'Customer-facing error shown when selected Ingresso Arena seats cannot be prepared for checkout',
      })
    case 'reserve-failed':
      switch (error.reservationFailureReason) {
        case 'leaves-single-seat':
          return intl.formatMessage({
            id: 'kaxmhfd07QcCg-be_1o7',
            defaultMessage: 'Those seats would leave a single seat unavailable. Please choose seats together and try again.',
            description: 'Customer-facing error shown when selected Ingresso Arena seats would leave a single seat unavailable',
          })
        case 'missing-transaction':
          return intl.formatMessage({
            id: '-3bkt_02hAECWFcBXoY3',
            defaultMessage: 'We couldn’t confirm your seat reservation. Please choose different seats and try again.',
            description: 'Customer-facing error shown when Ingresso Arena cannot confirm the seat reservation',
          })
        case 'seat-unavailable':
          return intl.formatMessage({
            id: 'dAQN20pKPTt19zmW9skw',
            defaultMessage: 'We couldn’t reserve one or more of those seats. Please choose different seats and try again.',
            description: 'Customer-facing error shown when one or more selected Ingresso Arena seats cannot be reserved',
          })
        case 'some-seats-unavailable':
          return intl.formatMessage({
            id: 'B6U28xEYnIVq1UPqhjNm',
            defaultMessage: 'Some of those seats could not be reserved. Please choose different seats and try again.',
            description: 'Customer-facing error shown when some selected Ingresso Arena seats cannot be reserved',
          })
        case 'reserve-failed':
        default:
          return intl.formatMessage({
            id: '4uFo60MiA7zqGhn-NLW4',
            defaultMessage: 'We couldn’t reserve those seats. Please choose different seats and try again.',
            description: 'Customer-facing error shown when selected Ingresso Arena seats cannot be reserved',
          })
      }
    case 'script-load-failed':
      return intl.formatMessage({
        id: 'XJMt-S1SewSdOx-tzLUP',
        defaultMessage: 'Arena could not load the seat map.',
        description: 'Error shown when the Ingresso Arena browser script cannot be loaded',
      })
    case 'selection-session-failed':
    default:
      return intl.formatMessage({
        id: 'hpHcAI-IXln_eZFC_kvw',
        defaultMessage: 'Arena could not create a selection session for this offer.',
        description: 'Error shown when the app cannot create an Ingresso Arena selection session',
      })
  }
}

function waitForTrolleyToken(resolverRef: React.MutableRefObject<((token: string) => void) | undefined>) {
  return new Promise<string | undefined>((resolve) => {
    const timeout = window.setTimeout(() => {
      if (resolverRef.current === resolveToken) resolverRef.current = undefined
      resolve(undefined)
    }, 5000)

    function resolveToken(token: string) {
      window.clearTimeout(timeout)
      resolve(token)
    }

    resolverRef.current = resolveToken
  })
}

function isReservationError(error: ArenaErrorState) {
  return error.type === 'missing-trolley-token' || error.type === 'reserve-failed'
}

function removeArenaSeat(root: ShadowRoot, seatUuid: string | undefined) {
  if (!seatUuid) return

  const mapSeat = root.querySelector<SVGElement>(`#arn-${seatUuid}`)
  mapSeat?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true }))
}

function ArenaSeatSelection(props: Props) {
  const { apiBase, displayCurrencyCode, eventId, experienceId, onSelectionComplete, options = defaultArenaOptions, perfId, scriptSrc = ARENA_SCRIPT_SRC, selectedTimeSlot, tyrBase } = props
  const intl = useIntl()
  const arenaElementRef = useRef<HTMLElement | null>(null)
  const arenaAppRef = useRef<ArenaApp | undefined>(undefined)
  const seatIconRef = useRef<HTMLSpanElement>(null)
  const presentationRef = useRef<ReturnType<typeof attachArenaSeatPresentation> | undefined>(undefined)
  const hoverLeaveTimer = useRef<number | undefined>(undefined)
  const displayCurrencyCodeRef = useRef(displayCurrencyCode)
  const selectedTimeSlotRef = useRef(selectedTimeSlot)
  const selectionSessionTokenRef = useRef<string | undefined>(undefined)
  const sendMethodRef = useRef<ArenaSendMethod | undefined>(undefined)
  const trolleyTokenRef = useRef<string | undefined>(undefined)
  const pendingTrolleyTokenResolverRef = useRef<((token: string) => void) | undefined>(undefined)
  const [currency, setCurrency] = useState<ArenaCurrency | undefined>()
  const [legend, setLegend] = useState<Array<ArenaSeatAvailabilityLegend>>([])
  const [selectedSeats, setSelectedSeats] = useState<Array<ArenaSelectedSeat>>([])
  const [allSeats, setAllSeats] = useState<Array<ArenaSelectedSeat>>([])
  const [hoverAnchor, setHoverAnchor] = useState<ArenaSeatAnchor | undefined>(undefined)
  const [, setTrolleyToken] = useState<string | undefined>()
  const [status, setStatus] = useState<ArenaStatus>('idle')
  const [error, setError] = useState<ArenaErrorState | undefined>()
  const [arenaResetKey, setArenaResetKey] = useState(0)
  const { mutateAsync: createSelectionSession } = useCreateExperienceSelectionSession()
  displayCurrencyCodeRef.current = displayCurrencyCode
  selectedTimeSlotRef.current = selectedTimeSlot

  const handleArenaEvent = useCallback((eventName: ArenaEventName, payload: ArenaEventPayload) => {
    const update = getArenaEventStateUpdate(eventName, payload)
    if (update.status) setStatus(update.status)
    if ('error' in update) {
      setError(update.error ? { type: update.error, eventName: update.failedEventName } : undefined)
    }
    if ('currency' in update) setCurrency(update.currency)
    if (update.legend) {
      const customLegend = getArenaCustomLegend(update.legend, selectedTimeSlotRef.current, displayCurrencyCodeRef.current)
      arenaAppRef.current?.setCustomLegend?.(customLegend)
      setLegend(update.legend)
    }
    if (update.seats) {
      setAllSeats(update.seats.map(seat => ({ ...seat, finance: seat.finance && { ...seat.finance }, seatBlock: seat.seatBlock && { ...seat.seatBlock } })))
    }
    if (update.selectedSeats) setSelectedSeats(update.selectedSeats)
    if ('sendMethod' in update) sendMethodRef.current = update.sendMethod
    if (update.shouldClearTrolleyToken) {
      trolleyTokenRef.current = undefined
      setTrolleyToken(undefined)
    }
    if ('trolleyToken' in update) {
      trolleyTokenRef.current = update.trolleyToken
      setTrolleyToken(update.trolleyToken)
      if (update.trolleyToken) {
        pendingTrolleyTokenResolverRef.current?.(update.trolleyToken)
        pendingTrolleyTokenResolverRef.current = undefined
      }
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    let arenaApp: ArenaApp | undefined
    const listeners = new Map<ArenaEventName, (payload: ArenaEventPayload) => void>()

    async function initialiseArena() {
      setStatus('creating-session')
      setError(undefined)
      setCurrency(undefined)
      setLegend([])
      setSelectedSeats([])
      setAllSeats([])
      setHoverAnchor(undefined)
      selectionSessionTokenRef.current = undefined
      sendMethodRef.current = undefined
      trolleyTokenRef.current = undefined
      pendingTrolleyTokenResolverRef.current = undefined
      setTrolleyToken(undefined)
      let selectionSession
      try {
        selectionSession = await createSelectionSession(experienceId)
      } catch {
        if (!cancelled) {
          setStatus('failed')
          setError({ type: 'selection-session-failed' })
        }
        return
      }

      if (cancelled) return
      selectionSessionTokenRef.current = selectionSession.sessionToken

      setStatus('loading-script')
      try {
        await loadArenaScript(scriptSrc)
      } catch {
        if (!cancelled) {
          setStatus('failed')
          setError({ type: 'script-load-failed' })
        }
        return
      }

      if (cancelled) return

      if (!window.IngressoArena) {
        setStatus('failed')
        setError({ type: 'arena-api-unavailable' })
        return
      }

      setStatus('creating-arena')
      let app
      try {
        app = await window.IngressoArena.create({
          apiAuthToken: selectionSession.sessionToken,
          apiBase,
          eventId,
          perfId,
          tyrBase,
          options,
        })
      } catch {
        if (!cancelled) {
          setStatus('failed')
          setError({ type: 'arena-create-failed' })
        }
        return
      }

      if (cancelled) {
        app.destroy?.()
        return
      }

      arenaAppRef.current = app
      arenaApp = app
      arenaEvents.forEach((eventName) => {
        const listener = (payload: ArenaEventPayload) => handleArenaEvent(eventName, payload)
        listeners.set(eventName, listener)
        app.on(eventName, listener)
      })
      setStatus('ready')
    }

    initialiseArena().catch(() => {
      if (!cancelled) {
        setStatus('failed')
        setError({ type: 'selection-session-failed' })
      }
    })

    return () => {
      cancelled = true
      listeners.forEach((listener, eventName) => {
        arenaApp?.off?.(eventName, listener)
      })
      listeners.clear()
      arenaApp?.destroy?.()
      arenaAppRef.current = undefined
      selectionSessionTokenRef.current = undefined
      sendMethodRef.current = undefined
      pendingTrolleyTokenResolverRef.current = undefined
    }
  }, [apiBase, arenaResetKey, createSelectionSession, eventId, experienceId, handleArenaEvent, options, perfId, scriptSrc, tyrBase])

  const totalPrice = useMemo(() => getArenaDisplayTotalPrice(selectedSeats, selectedTimeSlot), [selectedSeats, selectedTimeSlot])
  const hasMatchedTickets = useMemo(() => areAllArenaSeatsMatchedToTickets(selectedSeats, selectedTimeSlot), [selectedSeats, selectedTimeSlot])
  const pointsEarnCalculationRequests = useMemo(() => selectedSeats.flatMap((seat) => {
    const price = getArenaDisplaySeatPrice(seat, selectedTimeSlot)
    const request = generateLoyaltyPointsCalculatorExperienceOptions(undefined, {
      offerId: experienceId,
      price,
      skip: price === undefined,
    })
    return request ? [request] : []
  }), [experienceId, selectedSeats, selectedTimeSlot])
  const { totalPointsCalculation } = useLoyaltyPointsEarnCalculator(
    pointsEarnCalculationRequests,
    { useBatch: true },
  )
  const totalEarnablePoints = totalPointsCalculation.data?.points ?? 0
  const customLegend = useMemo(
    () => getArenaCustomLegend(legend, selectedTimeSlot, displayCurrencyCode).legendChanges,
    [displayCurrencyCode, legend, selectedTimeSlot],
  )
  const availabilityHeading = intl.formatMessage({
    id: 'AUYW4E_GlFaDvZY7slkT',
    defaultMessage: 'Availability',
    description: 'Section heading displayed above the Ingresso Arena seat availability card',
  })
  const seatPriceLegendLabel = intl.formatMessage({
    id: 'H25E5Y5XZ9wG8MDW8cz9',
    defaultMessage: 'Seat price legend',
    description: 'Accessible label for the custom Arena legend that explains seat category prices',
  })
  const heading = intl.formatMessage({
    id: 'TlWxNkjpLr8x3z-5BdbE',
    defaultMessage: 'Select your seats',
    description: 'Heading for the Ingresso Arena seat selection component on the experience offer page',
  })
  const selectedSeatsHeading = intl.formatMessage({
    id: '0KUh2PHni3PgNeT5SN_U',
    defaultMessage: 'Selected seats',
    description: 'Heading for the selected seats summary below the Ingresso Arena map',
  })
  const restrictedViewLabel = intl.formatMessage({
    id: 'TIiNcd6AcVWfarEOs2gm',
    defaultMessage: 'Restricted view',
    description: 'Short warning label shown in the Ingresso Arena seat hover when a seat has a restricted view',
  })
  const removeSelectedSeatLabel = intl.formatMessage({
    id: 'BfzMVc4j4S4TCkljQ45q',
    defaultMessage: 'Remove selected seat',
    description: 'Accessible label for the non-interactive selected Arena seat removal control shown in the seat summary',
  })
  const handleReservationError = useCallback((nextError: ArenaErrorState) => {
    setStatus('failed')
    setError(nextError)
    showSnackbar(getErrorLabel(intl, nextError), 'critical')
    setArenaResetKey(key => key + 1)
  }, [intl])

  const seatMap = useMemo(() => {
    const seats = new Map(allSeats.filter(seat => seat.uuid).map(seat => [seat.uuid!, seat]))
    selectedSeats.forEach(seat => { if (seat.uuid) seats.set(seat.uuid, { ...seats.get(seat.uuid), ...seat }) })
    return seats
  }, [allSeats, selectedSeats])
  const selectedIds = useMemo(() => new Set(selectedSeats.flatMap(seat => seat.uuid ? [seat.uuid] : [])), [selectedSeats])
  const modelRef = useRef({ seats: seatMap, selectedIds })
  modelRef.current = { seats: seatMap, selectedIds }
  const cancelHoverLeave = useCallback(() => window.clearTimeout(hoverLeaveTimer.current), [])
  const closeHover = useCallback(() => {
    cancelHoverLeave()
    setHoverAnchor(undefined)
  }, [cancelHoverLeave])
  const leaveHover = useCallback(() => {
    cancelHoverLeave()
    hoverLeaveTimer.current = window.setTimeout(closeHover, 300)
  }, [cancelHoverLeave, closeHover])
  const showHover = useCallback((anchor: ArenaSeatAnchor | undefined) => {
    cancelHoverLeave()
    setHoverAnchor(current => current?.element === anchor?.element ? current : anchor)
  }, [cancelHoverLeave])

  useEffect(() => presentationRef.current?.refresh(), [seatMap, selectedIds, displayCurrencyCode, selectedTimeSlot])

  useEffect(() => {
    let attempts = 0
    let timeout: number | undefined
    let detachMapReset: (() => void) | undefined

    function observeArenaShadowRoot() {
      const shadowRoot = arenaElementRef.current?.shadowRoot
      const checkIcon = seatIconRef.current?.querySelector('svg')
      if (!shadowRoot || !checkIcon) {
        attempts += 1
        if (attempts < 20) timeout = window.setTimeout(observeArenaShadowRoot, 250)
        return
      }

      detachMapReset = attachArenaMapReset(shadowRoot)
      presentationRef.current = attachArenaSeatPresentation(shadowRoot, {
        getSeats: () => modelRef.current.seats,
        getSelectedIds: () => modelRef.current.selectedIds,
        getSeatLabel: (seat) => {
          const price = getArenaDisplaySeatPrice(seat, selectedTimeSlotRef.current)
          const label = [seat.label ?? seat.seatId, seat.ticketTypeDesc ?? seat.seatBlock?.desc].filter(Boolean).join(' · ')
          return intl.formatList([
            label,
            ...(price === undefined ? [] : [intl.formatNumber(price, { style: 'currency', currency: displayCurrencyCodeRef.current })]),
            ...(seat.isRestrictedView ? [restrictedViewLabel] : []),
          ], { type: 'conjunction' })
        },
        checkIcon,
        onHover: showHover,
        onLeave: leaveHover,
      })
    }

    observeArenaShadowRoot()

    return () => {
      detachMapReset?.()
      presentationRef.current?.destroy()
      presentationRef.current = undefined
      if (timeout) window.clearTimeout(timeout)
      cancelHoverLeave()
    }
  }, [arenaResetKey, status, showHover, leaveHover, cancelHoverLeave, intl, restrictedViewLabel])

  const handleContinue = useCallback(async() => {
    if (!arenaAppRef.current || selectedSeats.length === 0) return

    setStatus('reserving')
    setError(undefined)
    const reservedSeats = selectedSeats

    try {
      let nextTrolleyToken: string | undefined
      let reservationHold: ArenaReservationHold | undefined

      if (hasMixedArenaReservableSeatGroups(reservedSeats)) {
        if (!selectionSessionTokenRef.current || !sendMethodRef.current) {
          handleReservationError({ type: 'reserve-failed' })
          return
        }

        nextTrolleyToken = await createArenaTrolleyToken({
          apiAuthToken: selectionSessionTokenRef.current,
          apiBase,
          perfId,
          selectedSeats: reservedSeats,
          sendMethod: sendMethodRef.current,
        })
      } else {
        const reserveResult = await arenaAppRef.current.reserveSeats?.()
        if (reserveResult && typeof reserveResult !== 'string') {
          reservationHold = getArenaReservationHold(reserveResult)
          if (!reservationHold) {
            const transactionUuid = reserveResult.trolley_contents?.transaction_uuid
            if (transactionUuid && selectionSessionTokenRef.current) {
              await releaseArenaTransaction({
                apiAuthToken: selectionSessionTokenRef.current,
                apiBase,
                transactionUuid,
              })
            }

            handleReservationError({
              type: 'reserve-failed',
              reservationFailureReason: getArenaReservationFailureReason(reserveResult),
            })
            return
          }

          nextTrolleyToken = getTrolleyToken(reserveResult)
        } else {
          nextTrolleyToken = getTrolleyToken(reserveResult) ?? trolleyTokenRef.current ?? await waitForTrolleyToken(pendingTrolleyTokenResolverRef)
        }
      }

      if (!nextTrolleyToken) {
        handleReservationError({ type: 'missing-trolley-token' })
        return
      }

      if (!reservationHold) {
        if (!selectionSessionTokenRef.current || !sendMethodRef.current) {
          handleReservationError({ type: 'reserve-failed' })
          return
        }

        const reservation = await createArenaTrolleyReservation({
          apiAuthToken: selectionSessionTokenRef.current,
          apiBase,
          sendMethod: sendMethodRef.current,
          trolleyToken: nextTrolleyToken,
        })
        if (!reservation.isValid) {
          handleReservationError({ type: 'reserve-failed', reservationFailureReason: reservation.failureReason })
          return
        }

        reservationHold = {
          reservationExpiresAt: reservation.reservationExpiresAt,
          reservationToken: reservation.reservationToken,
        }
      }

      trolleyTokenRef.current = nextTrolleyToken
      setTrolleyToken(nextTrolleyToken)
      setStatus('reserved')
      onSelectionComplete({
        currency,
        reservationExpiresAt: reservationHold.reservationExpiresAt,
        reservationToken: reservationHold.reservationToken,
        selectedSeats: reservedSeats,
        trolleyToken: nextTrolleyToken,
        totalPrice,
      })
    } catch {
      handleReservationError({ type: 'reserve-failed' })
    }
  }, [apiBase, currency, handleReservationError, onSelectionComplete, perfId, selectedSeats, totalPrice])

  const handleSeatRemove = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const shadowRoot = arenaElementRef.current?.shadowRoot
    if (!shadowRoot) return

    removeArenaSeat(shadowRoot, event.currentTarget.dataset.seatUuid)
  }, [])

  const hoveredSeat = hoverAnchor && seatMap.get(hoverAnchor.uuid)
  const preparingMap = status === 'idle' || status === 'creating-session' || status === 'loading-script'
  const loadingLabel = intl.formatMessage({
    id: 'muk5vPa34bLQS3ZsgMws',
    defaultMessage: 'Loading seat map',
    description: 'Accessible loading status while preparing the Arena seating map and selection session',
  })

  return (
    <Container data-testid="arena-seat-selection">
      <span ref={seatIconRef} hidden><LineCheckIcon /></span>
      {hoverAnchor && hoveredSeat && <ArenaSeatHoverCard anchor={hoverAnchor} seat={hoveredSeat} selectedTimeSlot={selectedTimeSlot} currency={displayCurrencyCode} restrictedViewLabel={restrictedViewLabel} onClose={closeHover} onEnter={cancelHoverLeave} onLeave={leaveHover} />}
      <Shell>
        <AvailabilityHeading variant="heading4">{availabilityHeading}</AvailabilityHeading>
        <Header>
          <Heading variant="heading5">{heading}</Heading>
          {error && !isReservationError(error) && <ErrorPill>
            <LineExclamationCircleIcon colour="warning" size="S" />
            <BodyText variant="small" weight="bold">
              {getErrorLabel(intl, error)}
            </BodyText>
          </ErrorPill>}
        </Header>
        <Content>
          <ArenaPanel data-testid="arena-map-panel">
            {customLegend.length > 0 && <ArenaLegend aria-label={seatPriceLegendLabel}>
              {customLegend.map((item, index) => item.price !== undefined && <ArenaLegendItem key={`${item.ticketTypeCode}-${item.priceBandCode}-${item.discountCode}`}>
                <ColourSwatch $bgColor={defaultArenaOptions.legendColours?.[index]?.bgColor} $borderColor={defaultArenaOptions.legendColours?.[index]?.borderColor} $size={16} />
                <BodyText variant="medium"><FormatCurrency value={item.price} currency={displayCurrencyCode} /></BodyText>
              </ArenaLegendItem>)}
            </ArenaLegend>}
            {React.createElement('igo-arena', { key: arenaResetKey, ref: arenaElementRef })}
            {preparingMap && <MapLoading role="status" aria-label={loadingLabel} data-testid="arena-map-loading"><LoadingIndicator inline /></MapLoading>}
          </ArenaPanel>
          {selectedSeats.length > 0 && (
            <SelectedSeatsSection aria-labelledby="arena-selected-seats-heading" data-testid="arena-selected-seats">
              <Heading id="arena-selected-seats-heading" variant="heading5">{selectedSeatsHeading}</Heading>
              <SeatList>
                {selectedSeats.map((seat) => {
                  const seatPrice = getArenaDisplaySeatPrice(seat, selectedTimeSlot)
                  const seatLabel = [seat.label ?? seat.seatId, seat.ticketTypeDesc ?? seat.seatBlock?.desc].filter(Boolean).join(' · ')
                  return (
                    <SeatItem key={seat.uuid ?? seat.seatId}>
                      <ColourSwatch $bgColor={seat.bgColor} $borderColor={seat.borderColor} $restricted={seat.isRestrictedView} />
                      <SeatDetails>
                        <BodyText variant="large" weight="bold">{seatLabel}</BodyText>
                        {seat.isRestrictedView && <BodyText variant="small">{restrictedViewLabel}</BodyText>}
                      </SeatDetails>
                      {seatPrice !== undefined && (
                        <SeatPrice variant="large">
                          <FormatCurrency value={seatPrice} currency={displayCurrencyCode} />
                        </SeatPrice>
                      )}
                      <SeatRemoveButton aria-label={removeSelectedSeatLabel} data-seat-uuid={seat.uuid} kind="secondary" onClick={handleSeatRemove} shape="circle" size="medium" variant="default">
                        <LineTrashAltIcon />
                      </SeatRemoveButton>
                    </SeatItem>
                  )
                })}
              </SeatList>
            </SelectedSeatsSection>
          )}
          <ArenaBookingFooter
            totalPrice={totalPrice}
            displayCurrencyCode={displayCurrencyCode}
            earnablePoints={totalEarnablePoints}
            disabled={selectedSeats.length === 0 || !hasMatchedTickets || status === 'reserving'}
            onContinue={handleContinue}
          />
        </Content>
      </Shell>
    </Container>
  )
}

export default React.memo(ArenaSeatSelection)
